import React, { useState, useEffect, useCallback } from 'react';
import { MicIcon, PauseIcon, Loader2, Sparkles, Save, User, Clock, Crown, LockIcon, UserPlus, Heart } from 'lucide-react';
import { useElevenLabsAI } from '../hooks/useElevenLabsAI';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { useTrial } from '../contexts/TrialContext';
import { saveUserManifestation } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { ShimmerButton } from './ShimmerButton';

export const ManifestationCoach: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { 
    hasTriedBefore,
    isFirstTimeUser,
    canTryNow,
    showRegistrationPrompt,
    markAsTriedOnce,
    startFirstTrial,
    hideRegistrationPrompt,
    isLoading: trialLoading
  } = useTrial();
  
  const [phase, setPhase] = useState<'initial' | 'formulation' | 'conviction' | 'complete'>('initial');
  const [manifestation, setManifestation] = useState('');
  const [convictionLevel, setConvictionLevel] = useState(0);
  const [showManifestation, setShowManifestation] = useState(false);
  const [manifestationSaved, setManifestationSaved] = useState(false);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(true);
  
  // Manifestation callback function
  const handleManifestationGenerated = useCallback((newManifestation: string) => {
    // Reset states for new manifestation
    setShowManifestation(false);
    setManifestationSaved(false);
    setShowSavePrompt(false);
    
    // Set the new manifestation
    setManifestation(newManifestation);
    setPhase('conviction');
    setShowManifestation(true);
    
    // Show save prompt if user is not logged in
    if (!user) {
      setTimeout(() => setShowSavePrompt(true), 2000);
    }
  }, [user]);
  
  const { 
    isConnected, 
    isProcessing, 
    isSpeaking,
    status,
    startConversation, 
    stopConversation,
    userTranscript,
    aiTranscript,
    extractedManifestation,
    pendingManifestation,
    agreementDetected,
    error,
    isInitializing,
    // Talk time information
    dailyTalkTimeUsed,
    remainingTime,
    talkTimeWarning,
    formatTime
  } = useElevenLabsAI(undefined, handleManifestationGenerated);

  // Auto-save manifestation if user is logged in
  useEffect(() => {
    if (manifestation && user && !manifestationSaved && showManifestation) {
      saveManifestationToAccount();
    }
  }, [manifestation, user, manifestationSaved, showManifestation]);

  // Show warning when talk time is low
  useEffect(() => {
    if (talkTimeWarning && isProcessing) {
      toast('You have less than 30 seconds of talk time remaining!', {
        duration: 5000,
        icon: '⏱️',
        style: {
          background: '#fef3c7',
          color: '#92400e',
          border: '1px solid #fbbf24',
        },
      });
    }
  }, [talkTimeWarning, isProcessing]);

  // Mark as tried once when manifestation is complete (for non-logged-in users)
  useEffect(() => {
    if (!user && isFirstTimeUser && phase === 'complete') {
      markAsTriedOnce();
      // Gracefully hide welcome message after trial completion
      setTimeout(() => {
        setShowWelcomeMessage(false);
      }, 3000); // Wait 3 seconds after completion before hiding
    }
  }, [user, isFirstTimeUser, phase, markAsTriedOnce]);

  const saveManifestationToAccount = async () => {
    if (!user || !manifestation || manifestationSaved) return;
    
    try {
      await saveUserManifestation(user.id, manifestation, 'active');
      setManifestationSaved(true);
    } catch (error) {
      // Non-critical error, can be ignored
    }
  };

  const handleCircleClick = async () => {
    // If user is logged in, they can always use the coach
    if (user) {
      if (isProcessing) {
        stopConversation();
      } else {
        startConversation();
      }
      return;
    }
    
    // If user is not logged in but can still try (first time), start trial
    if (canTryNow) {
      startFirstTrial();
      if (isProcessing) {
        stopConversation();
      } else {
        startConversation();
      }
      return;
    }
    
    // If user has already tried, do nothing (button should be disabled)
    return;
  };

  const getButtonText = () => {
    if (isInitializing) return 'Connecting...';
    if (error) return t('manifestation.tapToStart');
    if (isProcessing) {
      if (isSpeaking) return t('manifestation.speaking');
      return t('manifestation.listening');
    }
    
    // Custom text based on user status
    if (!user && !canTryNow) return 'Try Manivo Once';
    if (!user && canTryNow) return 'Try Manivo Free';
    return t('manifestation.tapToStart');
  };

  const getStatusText = () => {
    if (isInitializing) return 'Preparing your manifestation experience...';
    if (error) return 'Tap to begin your manifestation journey';
    if (isProcessing) {
      if (isSpeaking) return t('manifestation.processing');
      return t('manifestation.listening');
    }
    
    // Custom status based on user state
    if (!user && !canTryNow) return 'You\'ve experienced Manivo! Sign up to continue your journey.';
    if (!user && canTryNow) return 'Try manifestation coaching once, completely free';
    return t('manifestation.guidance');
  };

  const handleConvictionClick = () => {
    const newLevel = Math.min(convictionLevel + 1, 10);
    setConvictionLevel(newLevel);
    
    if (newLevel >= 8) {
      setTimeout(() => {
        setPhase('complete');
      }, 500);
    }
  };

  // Get user's commitment tier
  const userProfile = (user as any)?.profile;
  const commitmentTier = userProfile?.commitment_tier || 'seeker';

  // Determine if the coach is usable
  const isCoachUsable = user || canTryNow;

  return (
    <div className="flex flex-col items-center space-y-12 py-8">
      {/* Talk Time Indicator for logged-in users */}
      {user && (
        <div className="bg-white/90 backdrop-blur-md rounded-2xl px-6 py-3 flex items-center space-x-4 shadow-lg border border-earth-clay/50">
          <div className="bg-earth-bronze/10 rounded-xl p-2">
            <Clock size={20} className="text-earth-bronze" />
          </div>
          <div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Daily Talk Time</div>
            <div className="flex items-center space-x-2 mt-1">
              <span className="text-lg font-bold text-earth-stone">{formatTime(remainingTime)}</span>
              {commitmentTier !== 'seeker' && (
                <span className="bg-earth-clay text-earth-stone text-xs px-2 py-0.5 rounded-full flex items-center">
                  <Crown size={10} className="mr-1" />
                  {commitmentTier}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

        {/* Dynamic Orb Container */}
      <div className="relative flex flex-col items-center py-8">
        {/* Main Dynamic Orb */}
        <div
          className={`dynamic-orb-container relative ${isCoachUsable ? 'cursor-pointer hover:scale-105' : 'opacity-50 cursor-not-allowed'} scale-90 sm:scale-100 md:scale-110 transition-transform duration-300`}
          onClick={isCoachUsable ? handleCircleClick : undefined}
        >
          {/* Background Orb with Dynamic Animation */}
          <div className={`dynamic-orb ${isProcessing ? 'active' : ''} ${isSpeaking ? 'speaking' : ''} ${error ? 'error' : ''}`}>
            <div className="orb-core"></div>
            <div className="orb-ring ring-1"></div>
            <div className="orb-ring ring-2"></div>
            <div className="orb-ring ring-3"></div>
            <div className="orb-pulse pulse-1"></div>
            <div className="orb-pulse pulse-2"></div>
            <div className="orb-pulse pulse-3"></div>
          </div>
          
          {/* Button Overlay */}
          <div className="orb-button-overlay">
            <div className="flex items-center space-x-2 sm:space-x-3 bg-white/90 backdrop-blur-sm rounded-full px-4 sm:px-6 py-2 sm:py-3 shadow-lg border border-white/20">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-black rounded-full flex items-center justify-center">
                {isInitializing || trialLoading ? (
                  <Loader2 size={14} className="sm:w-4 sm:h-4 text-white animate-spin" />
                ) : isProcessing ? (
                  isSpeaking ? (
                    <div className="speaking-indicator">
                      <div className="bar bar-1"></div>
                      <div className="bar bar-2"></div>
                      <div className="bar bar-3"></div>
                    </div>
                  ) : (
                    <div className="listening-indicator"></div>
                  )
                ) : !isCoachUsable ? (
                  <LockIcon size={14} className="sm:w-4 sm:h-4 text-white" />
                ) : (
                  <MicIcon size={14} className="sm:w-4 sm:h-4 text-white" />
                )}
              </div>
              <span className="text-gray-800 font-medium text-xs sm:text-sm">
                {getButtonText()}
              </span>
            </div>
          </div>
        </div>

        {/* Status Text */}
        <div className="mt-8 text-center">
          <p className="text-sm sm:text-base text-gray-700 max-w-xs sm:max-w-md px-4 leading-relaxed font-medium">
            {getStatusText()}
          </p>
        </div>
      </div>

      {/* User encouragement - show for both first-time and tried users */}
      {!user && showWelcomeMessage && (
        <div className="bg-gradient-to-br from-white via-earth-sand to-earth-clay border-2 border-earth-terracotta/20 rounded-2xl p-6 sm:p-8 text-center max-w-sm sm:max-w-md mx-auto shadow-xl">
          <div className="w-16 h-16 bg-gradient-to-br from-earth-bronze to-earth-terracotta rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg transform hover:rotate-12 transition-transform duration-300">
            <Heart size={28} className="text-white" />
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-earth-stone mb-3">
            Welcome to Manivo!
          </h3>
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
            Experience the power of AI-guided manifestation coaching. Try it once to see how it works!
          </p>
        </div>
      )}

      {/* Gentle Registration Prompt Modal */}
      {showRegistrationPrompt && !user && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-6">
          <div className="bg-white rounded-2xl max-w-sm sm:max-w-md w-full shadow-2xl mx-4">
            <div className="p-6 sm:p-8 text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-earth-bronze to-earth-terracotta rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart size={28} className="text-white" />
              </div>
              
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">
                Continue Your Journey
              </h2>
              
              <p className="text-sm sm:text-base text-gray-600 mb-6 leading-relaxed">
                You've experienced the power of Manivo! Join thousands of others on their manifestation journey with unlimited access to AI coaching, progress tracking, and a supportive community.
              </p>
              
              <div className="space-y-3 sm:space-y-4 mb-6">
                <div className="flex items-center text-left space-x-3">
                  <div className="w-6 h-6 bg-earth-moss/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-earth-moss text-sm">✓</span>
                  </div>
                  <span className="text-xs sm:text-sm text-gray-700">Unlimited AI manifestation coaching</span>
                </div>
                <div className="flex items-center text-left space-x-3">
                  <div className="w-6 h-6 bg-earth-moss/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-earth-moss text-sm">✓</span>
                  </div>
                  <span className="text-xs sm:text-sm text-gray-700">Save and track your manifestations</span>
                </div>
                <div className="flex items-center text-left space-x-3">
                  <div className="w-6 h-6 bg-earth-moss/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-earth-moss text-sm">✓</span>
                  </div>
                  <span className="text-xs sm:text-sm text-gray-700">Access to inspiration library</span>
                </div>
              </div>
              
              <div className="flex flex-col space-y-2 sm:space-y-3">
                <Link to="/register">
                  <ShimmerButton
                    shimmerColor="#ffffff"
                    shimmerDuration="2s"
                    background="linear-gradient(135deg, rgb(166 124 82) 0%, rgb(212 165 116) 100%)"
                    className="w-full font-medium text-sm sm:text-base py-2 sm:py-3"
                  >
                    <UserPlus size={16} className="sm:w-5 sm:h-5 mr-2" />
                    Continue Your Journey
                  </ShimmerButton>
                </Link>
                
                <Link 
                  to="/login"
                  className="w-full bg-white border border-gray-300 text-gray-700 px-4 py-2 sm:py-3 rounded-lg text-xs sm:text-sm font-medium hover:bg-gray-50 transition-colors duration-200 text-center"
                >
                  I Already Have an Account
                </Link>
                
                <button
                  onClick={hideRegistrationPrompt}
                  className="text-gray-500 text-xs sm:text-sm hover:text-gray-700 transition-colors duration-200"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Magical Manifestation Display */}
      {manifestation && (
        <div className={`manifestation-container ${showManifestation ? 'wish-granted' : ''} max-w-xs sm:max-w-lg lg:max-w-3xl text-center relative mx-4 sm:mx-auto`}>
          {/* Magic sparkles background */}
          <div className="sparkles-container absolute inset-0 pointer-events-none">
            <Sparkles className="sparkle sparkle-1 text-yellow-400" size={12} />
            <Sparkles className="sparkle sparkle-2 text-purple-400" size={10} />
            <Sparkles className="sparkle sparkle-3 text-pink-400" size={11} />
            <Sparkles className="sparkle sparkle-4 text-blue-400" size={8} />
            <Sparkles className="sparkle sparkle-5 text-yellow-300" size={14} />
            <Sparkles className="sparkle sparkle-6 text-purple-300" size={12} />
          </div>
          
          {/* Manifestation content */}
          <div className="relative bg-gradient-to-br from-white via-earth-sand/30 to-white backdrop-blur-md rounded-3xl p-6 sm:p-8 lg:p-12 shadow-2xl border-2 border-earth-terracotta/20">
            <div className="mb-6">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-yellow-400 via-yellow-500 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl transform hover:rotate-12 transition-transform duration-300">
                <Sparkles size={24} className="sm:w-7 sm:h-7 text-white" />
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-earth-stone mb-4 tracking-tight">Your Manifestation</h3>
            </div>
            
            <div className="bg-gradient-to-br from-white to-earth-sand rounded-2xl p-6 sm:p-8 mb-6 sm:mb-8 border-2 border-earth-terracotta/20 shadow-inner">
              <p className="text-lg sm:text-xl lg:text-2xl text-earth-stone leading-relaxed italic font-semibold">
                "{manifestation}"
              </p>
            </div>
            
            {phase !== 'complete' && (
              <div className="conviction-builder">
                <p className="text-sm sm:text-base text-gray-700 mb-6 font-medium">
                  How strongly do you believe this will manifest? (Click to increase your conviction)
                </p>
                
                <div
                  className="conviction-meter cursor-pointer mb-6"
                  onClick={handleConvictionClick}
                >
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm sm:text-base text-gray-600 font-medium">Conviction Level</span>
                    <span className="text-base sm:text-lg font-bold text-earth-bronze">{convictionLevel}/10</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 sm:h-4 overflow-hidden shadow-inner">
                    <div
                      className="h-full bg-gradient-to-r from-earth-bronze to-earth-terracotta rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${(convictionLevel / 10) * 100}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>Doubt</span>
                    <span>Absolute Certainty</span>
                  </div>
                </div>
                
                {convictionLevel >= 8 && (
                  <div className="mt-4 p-3 sm:p-4 bg-earth-moss/10 border border-earth-moss/30 rounded-lg">
                    <p className="text-earth-forest text-xs sm:text-sm">
                      ✨ Excellent! High conviction accelerates manifestation. Your belief is powerful!
                    </p>
                  </div>
                )}
              </div>
            )}
            
            {phase === 'complete' && (
              <div className="mt-8 completion-message">
                <div className="bg-gradient-to-r from-earth-sand to-earth-clay rounded-lg p-4 sm:p-6 border border-earth-terracotta/30">
                  <Sparkles className="inline-block text-gold-500 mb-2" size={20} />
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                    ✨ <strong>{t('manifestation.manifestationSet')}</strong> ✨
                    <br />
                    {t('manifestation.trustProcess')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Save Prompt for Non-Logged In Users */}
      {showSavePrompt && !user && manifestation && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-6">
          <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-sm sm:max-w-md w-full shadow-2xl border border-purple-100 mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-earth-bronze to-earth-terracotta rounded-full flex items-center justify-center mx-auto mb-4">
                <User size={24} className="text-white" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{t('manifestation.savePromptTitle')}</h3>
              <p className="text-sm sm:text-base text-gray-600 mb-6">
                {t('manifestation.savePromptMessage')}
              </p>
              
              <div className="bg-earth-clay rounded-lg p-3 sm:p-4 mb-6">
                <p className="text-xs sm:text-sm text-earth-stone italic">
                  "{manifestation}"
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={() => setShowSavePrompt(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                >
                  {t('manifestation.maybeLater')}
                </button>
                <Link
                  to="/register"
                  className="flex-1 bg-gradient-to-r from-earth-bronze to-earth-terracotta text-white px-4 py-2 rounded-lg text-xs sm:text-sm hover:from-earth-warmstone hover:to-earth-bronze transition-all duration-200 text-center"
                >
                  {t('manifestation.createAccount')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="text-center max-w-xs sm:max-w-md mx-4 sm:mx-auto">
          <p className="text-sm sm:text-base text-earth-bronze font-medium">{error}</p>
          <p className="text-xs sm:text-sm mt-2 text-gray-600">Tap the circle to begin your manifestation journey</p>
        </div>
      )}
    </div>
  );
};